import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useErrorReport } from '../../contexts/ErrorReportContext';
import { pauseOtherEventAudio } from '../../services/events/eventAudio';
import {
  listHubAudioTracks,
  getHubAudioStreamUrl,
} from './services/mp3PlayerApi';
import {
  HUB_AUDIO_CLASS,
  STREAM_URL_REFRESH_LEAD_MS,
  PLAYBACK_PERSIST_MS,
  canUseMp3Player,
  wrapPlaylistIndex,
  nextPlaylistIndex,
  shouldRefreshStreamUrl,
  shouldWarmStandby,
  shouldResumeOnForeground,
  canHandoffFromStandby,
  readStoredShade,
  writeStoredShade,
  readStoredRadioHidden,
  writeStoredRadioHidden,
  readStoredPlayback,
  writeStoredPlayback,
  resolveRestoredPlayback,
  isMissingCallableError,
  radioArtistLabel,
} from './services/mp3PlayerEngine';

const Mp3PlayerContext = createContext(null);

const EMPTY_STREAM = { trackId: null, url: '', expiresAt: 0 };

/** Tiny silent WAV so iOS can unlock both elements during the user gesture. */
const SILENT_UNLOCK_SRC =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

export function useMp3Player() {
  const ctx = useContext(Mp3PlayerContext);
  if (!ctx) throw new Error('useMp3Player must be used within Mp3PlayerProvider');
  return ctx;
}

export function Mp3PlayerProvider({ children }) {
  const { isAdmin, userProfile } = useAuth();
  const { reportError } = useErrorReport();

  const enabled = canUseMp3Player({
    isAdmin,
    shopOwned: userProfile?.shopOwned,
  });

  const audioARef = useRef(null);
  const audioBRef = useRef(null);
  /** Which element is active: 'a' | 'b' */
  const activeSlotRef = useRef('a');
  const tracksRef = useRef([]);
  const indexRef = useRef(0);
  const playingRef = useRef(false);
  const wantPlayingRef = useRef(false);
  const streamRef = useRef({ ...EMPTY_STREAM });
  const nextStreamRef = useRef({ ...EMPTY_STREAM });
  const playGenRef = useRef(0);
  const unlockedRef = useRef(false);
  const bgAdvanceLoggedRef = useRef(false);
  const warmGenRef = useRef(0);
  const currentTimeRef = useRef(0);
  const persistTimerRef = useRef(null);
  const lastPersistAtRef = useRef(0);
  /** True after a successful list (including an empty catalog). */
  const playlistReadyRef = useRef(false);
  const playlistFlightRef = useRef(null);
  const playlistGenRef = useRef(0);

  const [tracks, setTracks] = useState([]);
  const [index, setIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.8);
  const [shade, setShadeState] = useState(() => readStoredShade(true));
  const [loading, setLoading] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [streamExpiresAt, setStreamExpiresAt] = useState(0);
  const [hidden, setHidden] = useState(() => readStoredRadioHidden());

  tracksRef.current = tracks;
  indexRef.current = index;
  playingRef.current = isPlaying;

  const currentTrack = tracks[index] || null;

  const getActiveEl = useCallback(() => (
    activeSlotRef.current === 'a' ? audioARef.current : audioBRef.current
  ), []);

  const getStandbyEl = useCallback(() => (
    activeSlotRef.current === 'a' ? audioBRef.current : audioARef.current
  ), []);

  const clearAudioEl = useCallback((el) => {
    if (!el) return;
    el.pause();
    el.removeAttribute('src');
    el.load();
  }, []);

  const persistPlayback = useCallback((immediate = false) => {
    const write = () => {
      const track = tracksRef.current[indexRef.current];
      if (!track?.id) return;
      lastPersistAtRef.current = Date.now();
      writeStoredPlayback({
        trackId: track.id,
        currentTime: currentTimeRef.current,
      });
    };
    if (typeof window === 'undefined') {
      write();
      return;
    }
    if (immediate) {
      if (persistTimerRef.current) {
        window.clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
      write();
      return;
    }
    const elapsed = Date.now() - lastPersistAtRef.current;
    if (elapsed >= PLAYBACK_PERSIST_MS) {
      write();
      return;
    }
    if (persistTimerRef.current) return;
    persistTimerRef.current = window.setTimeout(() => {
      persistTimerRef.current = null;
      write();
    }, PLAYBACK_PERSIST_MS - elapsed);
  }, []);

  const loadPlaylist = useCallback(async ({ force = false } = {}) => {
    if (!force && playlistReadyRef.current) {
      return tracksRef.current;
    }
    if (!force && playlistFlightRef.current) {
      return playlistFlightRef.current;
    }

    const gen = ++playlistGenRef.current;
    let job;
    job = (async () => {
      setLoading(true);
      try {
        const list = await listHubAudioTracks();
        if (gen !== playlistGenRef.current) return tracksRef.current;
        const hadTracks = tracksRef.current.length > 0;
        const prevId = tracksRef.current[indexRef.current]?.id;
        setTracks(list);
        const stillThere = prevId ? list.findIndex((t) => t.id === prevId) : -1;
        if (hadTracks && stillThere >= 0) {
          setIndex(stillThere);
          indexRef.current = stillThere;
        } else {
          if (hadTracks) {
            clearAudioEl(audioARef.current);
            clearAudioEl(audioBRef.current);
            streamRef.current = { ...EMPTY_STREAM };
            nextStreamRef.current = { ...EMPTY_STREAM };
            wantPlayingRef.current = false;
            setIsPlaying(false);
            setBuffering(false);
          }
          const restored = resolveRestoredPlayback(list, readStoredPlayback());
          setIndex(restored.index);
          indexRef.current = restored.index;
          currentTimeRef.current = restored.currentTime;
          setCurrentTime(restored.currentTime);
          setDuration(Number(list[restored.index]?.durationSec) || 0);
        }
        playlistReadyRef.current = true;
        return list;
      } catch (err) {
        if (gen !== playlistGenRef.current) return tracksRef.current;
        if (isMissingCallableError(err)) {
          setTracks([]);
          playlistReadyRef.current = true;
        } else {
          playlistReadyRef.current = false;
          reportError(err, {
            pageLabel: 'Mp3Player',
            source: 'mp3-list',
            userMessage: 'Could not load playlist.',
          });
        }
        return tracksRef.current;
      } finally {
        if (gen === playlistGenRef.current) setLoading(false);
        if (playlistFlightRef.current === job) playlistFlightRef.current = null;
      }
    })();

    playlistFlightRef.current = job;
    return job;
  }, [reportError, clearAudioEl]);

  useEffect(() => {
    const a = audioARef.current;
    const b = audioBRef.current;
    if (a) a.volume = volume;
    if (b) b.volume = volume;
  }, [volume]);

  const mintStream = useCallback(async (trackId) => {
    const result = await getHubAudioStreamUrl(trackId);
    return {
      trackId,
      url: result.url,
      expiresAt: result.expiresAt,
    };
  }, []);

  const ensureStream = useCallback(
    async (trackId, { force = false } = {}) => {
      const cache = streamRef.current;
      const now = Date.now();
      if (
        !force
        && cache.trackId === trackId
        && cache.url
        && cache.expiresAt - now > STREAM_URL_REFRESH_LEAD_MS
      ) {
        return cache.url;
      }
      const next = nextStreamRef.current;
      if (
        !force
        && next.trackId === trackId
        && next.url
        && next.expiresAt - now > STREAM_URL_REFRESH_LEAD_MS
      ) {
        streamRef.current = { ...next };
        setStreamExpiresAt(next.expiresAt);
        return next.url;
      }
      const minted = await mintStream(trackId);
      streamRef.current = minted;
      setStreamExpiresAt(minted.expiresAt);
      return minted.url;
    },
    [mintStream],
  );

  const unlockBothElements = useCallback(async () => {
    if (unlockedRef.current) return;
    const els = [audioARef.current, audioBRef.current].filter(Boolean);
    await Promise.all(els.map(async (el) => {
      const wasMuted = el.muted;
      const hadSrc = Boolean(el.currentSrc || el.src);
      try {
        el.muted = true;
        if (!hadSrc) {
          el.src = SILENT_UNLOCK_SRC;
        }
        const p = el.play();
        if (p && typeof p.then === 'function') await p;
      } catch {
        /* gesture unlock best-effort */
      } finally {
        el.pause();
        el.muted = wasMuted;
        if (!hadSrc) {
          try {
            el.removeAttribute('src');
            el.load();
          } catch {
            /* ignore */
          }
        }
      }
    }));
    unlockedRef.current = true;
  }, []);

  const applySrcToEl = useCallback(async (el, url, { resumeTime = 0, play = false } = {}) => {
    if (!el || !url) return;
    if (el.src !== url) {
      el.src = url;
    }
    if (resumeTime > 0) {
      await new Promise((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          try {
            el.currentTime = resumeTime;
          } catch {
            /* ignore */
          }
          resolve();
        };
        if (el.readyState >= 1) {
          finish();
          return;
        }
        el.addEventListener('loadedmetadata', finish, { once: true });
        window.setTimeout(finish, 1500);
      });
    }
    if (play) {
      pauseOtherEventAudio(el);
      try {
        await el.play();
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        throw err;
      }
    }
  }, []);

  const warmStandby = useCallback(async () => {
    const list = tracksRef.current;
    if (!list.length || !wantPlayingRef.current) return;
    const nextIdx = nextPlaylistIndex(indexRef.current, list.length);
    const track = list[nextIdx];
    if (!track?.id) return;

    const gen = ++warmGenRef.current;
    try {
      const cache = nextStreamRef.current;
      const needsMint = shouldWarmStandby({
        wantPlaying: wantPlayingRef.current,
        trackId: track.id,
        url: cache.trackId === track.id ? cache.url : '',
        expiresAt: cache.trackId === track.id ? cache.expiresAt : 0,
      });
      let stream = cache.trackId === track.id ? cache : { ...EMPTY_STREAM };
      if (needsMint) {
        stream = await mintStream(track.id);
        if (gen !== warmGenRef.current) return;
        nextStreamRef.current = stream;
      }
      const standby = getStandbyEl();
      if (!standby || !stream.url) return;
      if (standby.src !== stream.url) {
        standby.src = stream.url;
        try {
          standby.load();
        } catch {
          /* ignore */
        }
      }
    } catch (err) {
      if (gen !== warmGenRef.current) return;
      if (err && err.name === 'AbortError') return;
    }
  }, [getStandbyEl, mintStream]);

  const logBackgroundAdvanceBlocked = useCallback(() => {
    if (bgAdvanceLoggedRef.current) return;
    bgAdvanceLoggedRef.current = true;
    reportError('Background next-track play blocked', {
      pageLabel: 'Mp3Player',
      source: 'mp3-background-advance',
      kind: 'intent',
      silent: true,
    });
  }, [reportError]);

  const playAtIndex = useCallback(
    async (nextIndex, { resumeTime = 0 } = {}) => {
      const list = tracksRef.current;
      if (!list.length) {
        reportError('No tracks in playlist', {
          pageLabel: 'Mp3Player',
          source: 'mp3-play',
          kind: 'intent',
          silent: true,
        });
        return;
      }
      const i = wrapPlaylistIndex(nextIndex, list.length, 0);
      const track = list[i];
      if (!track?.id) return;
      wantPlayingRef.current = true;
      const gen = ++playGenRef.current;
      setIndex(i);
      indexRef.current = i;
      currentTimeRef.current = resumeTime;
      setCurrentTime(resumeTime);
      persistPlayback(true);
      setBuffering(true);
      try {
        await unlockBothElements();
        if (gen !== playGenRef.current) return;
        const url = await ensureStream(track.id);
        if (gen !== playGenRef.current) return;
        const el = getActiveEl();
        await applySrcToEl(el, url, { resumeTime, play: true });
        if (gen !== playGenRef.current) return;
        warmStandby();
      } catch (err) {
        if (gen !== playGenRef.current) return;
        setBuffering(false);
        if (err && err.name === 'AbortError') return;
        if (err && err.name === 'NotAllowedError') {
          logBackgroundAdvanceBlocked();
          return;
        }
        reportError(err, {
          pageLabel: 'Mp3Player',
          source: 'mp3-play',
          userMessage: 'Could not play track.',
        });
      }
    },
    [
      applySrcToEl,
      ensureStream,
      getActiveEl,
      logBackgroundAdvanceBlocked,
      reportError,
      unlockBothElements,
      warmStandby,
      persistPlayback,
    ],
  );

  /**
   * Sync handoff: standby already has next src — play it without awaiting a callable.
   * @returns {boolean} true if handoff started
   */
  const handoffToStandby = useCallback((targetIndex) => {
    const list = tracksRef.current;
    if (!list.length) return false;
    const i = wrapPlaylistIndex(targetIndex, list.length, 0);
    const track = list[i];
    const standbyCache = nextStreamRef.current;
    if (
      !canHandoffFromStandby({
        standbyTrackId: standbyCache.trackId,
        standbyUrl: standbyCache.url,
        targetTrackId: track?.id,
        expiresAt: standbyCache.expiresAt,
      })
    ) {
      return false;
    }
    const standby = getStandbyEl();
    const active = getActiveEl();
    if (!standby || !standbyCache.url) return false;

    if (standby.src !== standbyCache.url) {
      standby.src = standbyCache.url;
    }

    wantPlayingRef.current = true;
    playGenRef.current += 1;
    setIndex(i);
    indexRef.current = i;
    setBuffering(true);
    currentTimeRef.current = 0;
    setCurrentTime(0);
    persistPlayback(true);

    // Swap slots before play so listeners attach to the new active.
    activeSlotRef.current = activeSlotRef.current === 'a' ? 'b' : 'a';
    streamRef.current = { ...standbyCache };
    setStreamExpiresAt(standbyCache.expiresAt);
    nextStreamRef.current = { ...EMPTY_STREAM };

    pauseOtherEventAudio(standby);
    const playPromise = standby.play();
    if (active && active !== standby) {
      try {
        active.pause();
      } catch {
        /* ignore */
      }
    }

    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((err) => {
        if (err && err.name === 'AbortError') return;
        if (err && err.name === 'NotAllowedError') {
          logBackgroundAdvanceBlocked();
          return;
        }
        reportError(err, {
          pageLabel: 'Mp3Player',
          source: 'mp3-handoff',
          userMessage: 'Could not play track.',
        });
      });
    }

    warmStandby();
    return true;
  }, [
    getActiveEl,
    getStandbyEl,
    logBackgroundAdvanceBlocked,
    reportError,
    warmStandby,
    persistPlayback,
  ]);

  const play = useCallback(() => {
    wantPlayingRef.current = true;
    const pendingUnlock = playlistReadyRef.current ? null : unlockBothElements();
    const run = async () => {
      if (pendingUnlock) {
        await pendingUnlock;
        await loadPlaylist();
        if (!wantPlayingRef.current || !playlistReadyRef.current) return;
      }
      const el = getActiveEl();
      const track = tracksRef.current[indexRef.current];
      if (!track) {
        playAtIndex(0);
        return;
      }
      if (el && streamRef.current.trackId === track.id && streamRef.current.url && !el.paused && el.src) {
        return;
      }
      const cache = streamRef.current;
      const needsUrl =
        cache.trackId !== track.id
        || !cache.url
        || shouldRefreshStreamUrl({
          isPlaying: true,
          expiresAt: cache.expiresAt,
        });
      if (el && !needsUrl && el.src && el.paused) {
        unlockBothElements().then(() => {
          pauseOtherEventAudio(el);
          const p = el.play();
          if (p && typeof p.catch === 'function') {
            p.catch((err) => {
              if (err && err.name === 'AbortError') return;
              if (err && err.name === 'NotAllowedError') {
                logBackgroundAdvanceBlocked();
                return;
              }
              reportError(err, {
                pageLabel: 'Mp3Player',
                source: 'mp3-play',
                userMessage: 'Could not play track.',
              });
            });
          }
          warmStandby();
        });
        return;
      }
      playAtIndex(indexRef.current, {
        resumeTime: el && el.src && streamRef.current.trackId === track.id
          ? (el.currentTime || 0)
          : currentTimeRef.current,
      });
    };
    run();
  }, [
    getActiveEl,
    loadPlaylist,
    logBackgroundAdvanceBlocked,
    playAtIndex,
    reportError,
    unlockBothElements,
    warmStandby,
  ]);

  const pause = useCallback(() => {
    wantPlayingRef.current = false;
    bgAdvanceLoggedRef.current = false;
    getActiveEl()?.pause();
    persistPlayback(true);
  }, [getActiveEl, persistPlayback]);

  const stop = useCallback(() => {
    wantPlayingRef.current = false;
    bgAdvanceLoggedRef.current = false;
    const el = getActiveEl();
    if (el) {
      el.pause();
      try {
        el.currentTime = 0;
      } catch {
        /* ignore */
      }
    }
    currentTimeRef.current = 0;
    setCurrentTime(0);
    persistPlayback(true);
  }, [getActiveEl, persistPlayback]);

  const show = useCallback(() => {
    setHidden(false);
    writeStoredRadioHidden(false);
  }, []);

  const close = useCallback(() => {
    stop();
    setHidden(true);
    writeStoredRadioHidden(true);
  }, [stop]);

  const togglePlay = useCallback(() => {
    if (playingRef.current) pause();
    else play();
  }, [pause, play]);

  const playNext = useCallback(() => {
    wantPlayingRef.current = true;
    const pendingUnlock = playlistReadyRef.current ? null : unlockBothElements();
    const run = async () => {
      if (pendingUnlock) {
        await pendingUnlock;
        await loadPlaylist();
        if (!wantPlayingRef.current || !playlistReadyRef.current) return;
      }
      const list = tracksRef.current;
      if (!list.length) return;
      const nextIdx = nextPlaylistIndex(indexRef.current, list.length);
      if (handoffToStandby(nextIdx)) return;
      playAtIndex(nextIdx);
    };
    run();
  }, [handoffToStandby, loadPlaylist, playAtIndex, unlockBothElements]);

  const playPrev = useCallback(() => {
    wantPlayingRef.current = true;
    const pendingUnlock = playlistReadyRef.current ? null : unlockBothElements();
    const run = async () => {
      if (pendingUnlock) {
        await pendingUnlock;
        await loadPlaylist();
        if (!wantPlayingRef.current || !playlistReadyRef.current) return;
      }
      const list = tracksRef.current;
      if (!list.length) return;
      const el = getActiveEl();
      if (el && el.currentTime > 3) {
        try {
          el.currentTime = 0;
        } catch {
          /* ignore */
        }
        currentTimeRef.current = 0;
        setCurrentTime(0);
        persistPlayback(true);
        return;
      }
      playAtIndex(wrapPlaylistIndex(indexRef.current, list.length, -1));
    };
    run();
  }, [getActiveEl, loadPlaylist, playAtIndex, persistPlayback, unlockBothElements]);

  const seek = useCallback((seconds) => {
    const next = Math.max(0, Number(seconds) || 0);
    currentTimeRef.current = next;
    setCurrentTime(next);
    persistPlayback(true);
    const el = getActiveEl();
    if (!el) return;
    try {
      el.currentTime = next;
    } catch {
      /* ignore */
    }
  }, [getActiveEl, persistPlayback]);

  const setVolume = useCallback((value) => {
    const next = Math.min(1, Math.max(0, Number(value) || 0));
    setVolumeState(next);
    if (audioARef.current) audioARef.current.volume = next;
    if (audioBRef.current) audioBRef.current.volume = next;
  }, []);

  const setShade = useCallback((next) => {
    const shadeOn = Boolean(next);
    setShadeState(shadeOn);
    writeStoredShade(shadeOn);
  }, []);

  const toggleShade = useCallback(() => {
    setShade(!shade);
  }, [setShade, shade]);

  const selectTrack = useCallback(
    (nextIndex) => {
      wantPlayingRef.current = true;
      playAtIndex(nextIndex);
    },
    [playAtIndex],
  );

  const resumeIfNeeded = useCallback(() => {
    if (
      !shouldResumeOnForeground({
        wantPlaying: wantPlayingRef.current,
        isPlaying: playingRef.current,
        documentVisible: typeof document === 'undefined'
          ? true
          : document.visibilityState === 'visible',
      })
    ) {
      return;
    }
    const list = tracksRef.current;
    if (!list.length) return;
    const el = getActiveEl();
    // If current ended and standby was blocked, advance; else resume current.
    if (el && el.ended) {
      playNext();
      return;
    }
    play();
  }, [getActiveEl, play, playNext]);

  // Bind both elements so handoff play/playing events are not missed after slot swap.
  useEffect(() => {
    const els = [audioARef.current, audioBRef.current].filter(Boolean);
    if (!els.length) return undefined;

    const isActiveTarget = (target) => target === getActiveEl();

    const onPlay = (e) => {
      if (!isActiveTarget(e.target)) return;
      pauseOtherEventAudio(e.target);
      setIsPlaying(true);
      bgAdvanceLoggedRef.current = false;
    };
    const onPlaying = (e) => {
      if (!isActiveTarget(e.target)) return;
      setBuffering(false);
      warmStandby();
    };
    const onWaiting = (e) => {
      if (!isActiveTarget(e.target)) return;
      setBuffering(true);
    };
    const onPause = (e) => {
      if (!isActiveTarget(e.target)) return;
      setIsPlaying(false);
      if (!e.target.currentSrc) return;
      persistPlayback(true);
    };
    const onTime = (e) => {
      if (!isActiveTarget(e.target)) return;
      if (!e.target.currentSrc) return;
      const t = e.target.currentTime || 0;
      currentTimeRef.current = t;
      setCurrentTime(t);
      persistPlayback(false);
    };
    const onMeta = (e) => {
      if (!isActiveTarget(e.target)) return;
      setDuration(Number.isFinite(e.target.duration) ? e.target.duration : 0);
    };
    const onEnded = (e) => {
      // Ending element was active; do not clear wantPlaying.
      if (!wantPlayingRef.current) return;
      if (e.target !== audioARef.current && e.target !== audioBRef.current) return;
      const list = tracksRef.current;
      if (!list.length) return;
      const nextIdx = nextPlaylistIndex(indexRef.current, list.length);
      if (handoffToStandby(nextIdx)) return;
      playAtIndex(nextIdx);
    };
    const onError = (e) => {
      if (!isActiveTarget(e.target)) return;
      setBuffering(false);
      if (!e.target.currentSrc) return;
      reportError('Track failed to load', {
        pageLabel: 'Mp3Player',
        source: 'mp3-audio',
        userMessage: 'Could not play track.',
      });
    };

    els.forEach((el) => {
      el.addEventListener('play', onPlay);
      el.addEventListener('playing', onPlaying);
      el.addEventListener('waiting', onWaiting);
      el.addEventListener('pause', onPause);
      el.addEventListener('timeupdate', onTime);
      el.addEventListener('loadedmetadata', onMeta);
      el.addEventListener('ended', onEnded);
      el.addEventListener('error', onError);
    });
    return () => {
      els.forEach((el) => {
        el.removeEventListener('play', onPlay);
        el.removeEventListener('playing', onPlaying);
        el.removeEventListener('waiting', onWaiting);
        el.removeEventListener('pause', onPause);
        el.removeEventListener('timeupdate', onTime);
        el.removeEventListener('loadedmetadata', onMeta);
        el.removeEventListener('ended', onEnded);
        el.removeEventListener('error', onError);
      });
    };
  }, [getActiveEl, handoffToStandby, playAtIndex, persistPlayback, reportError, warmStandby]);

  // Refresh active + standby signed URLs while the session is active.
  useEffect(() => {
    if (!isPlaying && !wantPlayingRef.current) return undefined;
    const id = window.setInterval(() => {
      const activeCache = streamRef.current;
      if (
        shouldRefreshStreamUrl({
          isPlaying: playingRef.current || wantPlayingRef.current,
          expiresAt: activeCache.expiresAt,
        })
      ) {
        const track = tracksRef.current[indexRef.current];
        if (track?.id && activeCache.trackId === track.id) {
          const el = getActiveEl();
          const resumeTime = el ? el.currentTime : 0;
          ensureStream(track.id, { force: true })
            .then((url) => applySrcToEl(el, url, {
              resumeTime,
              play: playingRef.current || wantPlayingRef.current,
            }))
            .catch((err) => {
              reportError(err, {
                pageLabel: 'Mp3Player',
                source: 'mp3-refresh',
                userMessage: 'Playback URL expired.',
              });
            });
        }
      }
      if (
        shouldWarmStandby({
          wantPlaying: wantPlayingRef.current,
          trackId: tracksRef.current[
            nextPlaylistIndex(indexRef.current, tracksRef.current.length)
          ]?.id,
          url: nextStreamRef.current.url,
          expiresAt: nextStreamRef.current.expiresAt,
        })
      ) {
        warmStandby();
      }
    }, 30000);
    return () => window.clearInterval(id);
  }, [isPlaying, ensureStream, applySrcToEl, getActiveEl, reportError, warmStandby]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') resumeIfNeeded();
      else persistPlayback(true);
    };
    const onPageShow = () => resumeIfNeeded();
    const onPageHide = () => persistPlayback(true);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('pagehide', onPageHide);
      if (persistTimerRef.current) {
        window.clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
    };
  }, [resumeIfNeeded, persistPlayback]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return undefined;
    const title = currentTrack?.title || 'MapToBe';
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title,
        artist: radioArtistLabel(currentTrack),
        album: 'MapToBe Radio',
      });
      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
      if (
        Number.isFinite(duration)
        && duration > 0
        && Number.isFinite(currentTime)
        && typeof navigator.mediaSession.setPositionState === 'function'
      ) {
        navigator.mediaSession.setPositionState({
          duration,
          position: Math.min(currentTime, duration),
          playbackRate: 1,
        });
      }
    } catch {
      /* ignore */
    }
    const handlers = [
      ['play', play],
      ['pause', pause],
      ['previoustrack', playPrev],
      ['nexttrack', playNext],
      ['stop', stop],
    ];
    handlers.forEach(([action, handler]) => {
      try {
        navigator.mediaSession.setActionHandler(action, handler);
      } catch {
        /* unsupported action */
      }
    });
    return () => {
      handlers.forEach(([action]) => {
        try {
          navigator.mediaSession.setActionHandler(action, null);
        } catch {
          /* ignore */
        }
      });
    };
  }, [
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    play,
    pause,
    playPrev,
    playNext,
    stop,
  ]);

  const value = useMemo(
    () => ({
      enabled,
      isAdmin,
      tracks,
      index,
      currentTrack,
      isPlaying,
      currentTime,
      duration,
      volume,
      shade,
      loading,
      buffering,
      streamExpiresAt,
      hidden,
      play,
      pause,
      stop,
      togglePlay,
      playNext,
      playPrev,
      seek,
      setVolume,
      setShade,
      toggleShade,
      selectTrack,
      reloadPlaylist: loadPlaylist,
      show,
      close,
    }),
    [
      enabled,
      isAdmin,
      tracks,
      index,
      currentTrack,
      isPlaying,
      currentTime,
      duration,
      volume,
      shade,
      loading,
      buffering,
      streamExpiresAt,
      hidden,
      play,
      pause,
      stop,
      togglePlay,
      playNext,
      playPrev,
      seek,
      setVolume,
      setShade,
      toggleShade,
      selectTrack,
      loadPlaylist,
      show,
      close,
    ],
  );

  const audioStyle = { display: 'none' };

  return (
    <Mp3PlayerContext.Provider value={value}>
      {children}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioARef}
        className={HUB_AUDIO_CLASS}
        preload="auto"
        playsInline
        style={audioStyle}
      />
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioBRef}
        className={HUB_AUDIO_CLASS}
        preload="auto"
        playsInline
        style={audioStyle}
      />
    </Mp3PlayerContext.Provider>
  );
}
